import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconChevronLeft } from "@/components/_icons/IconChevronLeft";
import { IconChevronRight } from "@/components/_icons/IconChevronRight";
import { useDate } from "@/hooks/useDate";
import { cn } from "@/lib/cn";
import { DPMonth, DPOffsetValue, useDatePicker } from "@rehookify/datepicker";

import { Button } from "./components/button";
import { Calendar } from "./components/calendar";
import { getDayClassName, getMonthClassName, getYearsClassName } from "./components/classnames-utils";
import { Section } from "./components/section";
import { SectionHeader } from "./components/section-header";

type ViewMode = "DAY" | "MONTH" | "YEAR";

const isSameDate = (left: Date | null, right: Date | null) => {
  if (left === null || right === null) {
    return left === right;
  }
  return left.getTime() === right.getTime();
};

export interface DatepickerProps {
  onValueChange?: (value: Date | null) => void;
  defaultValue?: Date;
  minDate?: Date;
  maxDate?: Date;
}

// Currently only built for selecting a single date
export const Datepicker: FC<DatepickerProps> = ({ onValueChange, defaultValue, minDate, maxDate }) => {
  const { t, i18n } = useTranslation("common");
  const { TODAY } = useDate();
  const [viewMode, setViewMode] = useState<ViewMode>("DAY");

  const [selectedDate, setSelectedDate] = useState<Date | null>(defaultValue ? defaultValue : null);
  const [selectedDateInternal, setSelectedDateInternal] = useState<Date | null>(defaultValue ? defaultValue : null);
  // the offsetDate is the that the calendar displays, not neccessary the selected date.
  const [offsetDate, setOffsetDate] = useState<Date>(defaultValue ? defaultValue : TODAY);
  const lastEmittedDate = useRef<Date | null>(selectedDate);

  // Focus management is handled by the parent InputDate component

  const selectedDates = useMemo(() => {
    if (!selectedDateInternal) {
      return [];
    }
    return [selectedDateInternal];
  }, [selectedDateInternal]);

  useEffect(() => {
    if (!onValueChange || isSameDate(lastEmittedDate.current, selectedDate)) {
      return;
    }
    lastEmittedDate.current = selectedDate;
    onValueChange(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleDateChange = () => {
    // callback needs to be set;
    // however we handle the selected Date state internally
  };

  const isValidDate = (date: Date) => {
    if (minDate && date.getTime() < minDate.getTime()) {
      return false;
    }
    if (maxDate && date.getTime() > maxDate.getTime()) {
      return false;
    }
    return true;
  };

  const dateWithOffset = (date: Date, offsetValue: DPOffsetValue) => {
    let newDate = new Date(date);
    if (offsetValue.days) {
      newDate = new Date(offsetDate.setDate(offsetDate.getDate() + offsetValue.days));
    }
    if (offsetValue.months) {
      newDate = new Date(offsetDate.setMonth(offsetDate.getMonth() + offsetValue.months));
    }
    if (offsetValue.years) {
      newDate = new Date(offsetDate.setFullYear(offsetDate.getFullYear() + offsetValue.years));
    }

    return newDate;
  };

  const getLocalizedMonthString = (monthString: string) => {
    const mapping = new Map<string, string>([
      ["January", t("datepicker.months.january")],
      ["February", t("datepicker.months.february")],
      ["March", t("datepicker.months.march")],
      ["April", t("datepicker.months.april")],
      ["May", t("datepicker.months.may")],
      ["June", t("datepicker.months.june")],
      ["July", t("datepicker.months.july")],
      ["August", t("datepicker.months.august")],
      ["September", t("datepicker.months.september")],
      ["October", t("datepicker.months.october")],
      ["November", t("datepicker.months.november")],
      ["December", t("datepicker.months.december")],
    ]);

    // fallback
    return mapping.get(monthString) ?? monthString;
  };

  const handleMonthSelected = (month: number) => {
    setSelectedDateInternal(new Date(offsetDate.getFullYear(), month, 1));
    setOffsetDate(new Date(offsetDate.getFullYear(), month, 1));

    setViewMode("DAY");
  };

  const handleYearSelected = (year: number) => {
    setSelectedDateInternal(new Date(year, 0, 1));
    setOffsetDate(new Date(year, 0, 1));
    setViewMode("MONTH");
  };

  const handleOnOffsetChange = (offsetValue: DPOffsetValue) => {
    const newDate = dateWithOffset(offsetDate, offsetValue);
    setOffsetDate(newDate);
  };

  const wrappedMonth = (month: DPMonth) => {
    const isDisabled = !selectedDateInternal
      ? month.disabled
      : !isValidDate(
          new Date(selectedDateInternal.getFullYear(), month.$date.getMonth(), selectedDateInternal.getDate()),
        );
    const wrappedMonth = { ...month, disabled: isDisabled };

    return wrappedMonth;
  };

  const {
    data: { calendars, weekDays, months, years },
    propGetters: { nextYearsButton, previousYearsButton },
  } = useDatePicker({
    selectedDates,
    offsetDate: offsetDate ?? undefined,
    onDatesChange: handleDateChange,
    calendar: {
      startDay: 1,
    },
    locale: {
      locale: i18n.language,
      weekday: "short",
      monthName: "long",
    },
    dates: {
      mode: "single",
      minDate: minDate,
      maxDate: maxDate,
    },
  });

  const { month, year, days } = calendars[0];

  const handleKeyDown = (event: React.KeyboardEvent, viewMode: ViewMode) => {
    switch (event.key) {
      case "Escape":
        // Let Radix UI handle closing the popover naturally
        break;
      case "Tab": {
        // Focus should move between the top controls: previous, month, next, year
        // Find all focusable top controls in order
        event.preventDefault();

        const previousMonthLabel = t("datepicker.previousMonth");
        const nextMonthLabel = t("datepicker.nextMonth");
        const currentMonthPrefix = t("datepicker.currentMonth", { month: "" }).split(":")[0].trim();
        const currentYearPrefix = t("datepicker.currentYear", { year: "" }).split(":")[0].trim();
        const topControls = [
          // Previous month button (only when in day view)
          ...(viewMode === "DAY" ? [document.querySelector(`[aria-label='${previousMonthLabel}']`)] : []),
          // Month selector button
          document.querySelector(`[aria-label*='${currentMonthPrefix}']`),
          // Next month button (only when in day view)
          ...(viewMode === "DAY" ? [document.querySelector(`[aria-label='${nextMonthLabel}']`)] : []),
          // Year selector button
          document.querySelector(`[aria-label*='${currentYearPrefix}']`),
        ].filter(Boolean) as HTMLElement[];
        const currentFocusedControl = document.activeElement;
        const currentIndex = topControls.findIndex((control) => control === currentFocusedControl);

        if (event.shiftKey) {
          // Shift+Tab: Move backward
          if (currentIndex > 0) {
            topControls[currentIndex - 1].focus();
          } else if (currentIndex === 0) {
            // Wrap around to last control
            topControls[topControls.length - 1].focus();
          } else {
            // If no control is focused, focus the last one
            topControls[topControls.length - 1]?.focus();
          }
        } else {
          // Tab: Move forward
          if (currentIndex >= 0 && currentIndex < topControls.length - 1) {
            // Move to next control
            topControls[currentIndex + 1].focus();
          } else if (currentIndex === topControls.length - 1) {
            // Move to first control (wrap around)
            topControls[0].focus();
          } else {
            // If no control is focused, focus the first one
            topControls[0]?.focus();
          }
        }
        break;
      }
      case "ArrowLeft":
      case "ArrowRight":
      case "ArrowUp":
      case "ArrowDown":
      case "Home":
      case "End":
        // Handle view-specific navigation with arrow keys
        event.preventDefault();
        if (viewMode === "DAY") {
          handleDayViewNavigation(event);
        } else if (viewMode === "MONTH") {
          handleMonthViewNavigation(event);
        } else if (viewMode === "YEAR") {
          handleYearViewNavigation(event);
        }
        break;
      default:
        break;
    }
  };

  const handleDayViewNavigation = (event: React.KeyboardEvent) => {
    const currentFocusedDay = document.activeElement;
    if (!currentFocusedDay || !currentFocusedDay.closest("[data-day]")) {
      // If no day is focused, focus the first available day
      const firstDay = document.querySelector("[data-day]:not([disabled])") as HTMLElement;
      if (firstDay) {
        firstDay.focus();
        return;
      }
      return;
    }

    const dayElements = Array.from(document.querySelectorAll("[data-day]:not([disabled])"));
    const currentIndex = dayElements.indexOf(currentFocusedDay as Element);

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          (dayElements[currentIndex - 1] as HTMLElement).focus();
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < dayElements.length - 1) {
          (dayElements[currentIndex + 1] as HTMLElement).focus();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (currentIndex >= 7) {
          (dayElements[currentIndex - 7] as HTMLElement).focus();
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        if (currentIndex + 7 < dayElements.length) {
          (dayElements[currentIndex + 7] as HTMLElement).focus();
        }
        break;
      case "Home":
        event.preventDefault();
        (dayElements[0] as HTMLElement).focus();
        break;
      case "End":
        event.preventDefault();
        (dayElements[dayElements.length - 1] as HTMLElement).focus();
        break;
    }
  };

  const handleMonthViewNavigation = (event: React.KeyboardEvent) => {
    const currentFocusedMonth = document.activeElement;
    if (!currentFocusedMonth || !currentFocusedMonth.closest("[data-month]")) {
      // If no month is focused, focus the first available month
      const firstMonth = document.querySelector("[data-month]:not([disabled])") as HTMLElement;
      if (firstMonth) {
        firstMonth.focus();
        return;
      }
      return;
    }

    const monthElements = Array.from(document.querySelectorAll("[data-month]:not([disabled])"));
    const currentIndex = monthElements.indexOf(currentFocusedMonth as Element);

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          (monthElements[currentIndex - 1] as HTMLElement).focus();
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < monthElements.length - 1) {
          (monthElements[currentIndex + 1] as HTMLElement).focus();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (currentIndex >= 3) {
          (monthElements[currentIndex - 3] as HTMLElement).focus();
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        if (currentIndex + 3 < monthElements.length) {
          (monthElements[currentIndex + 3] as HTMLElement).focus();
        }
        break;
      case "Home":
        event.preventDefault();
        (monthElements[0] as HTMLElement).focus();
        break;
      case "End":
        event.preventDefault();
        (monthElements[monthElements.length - 1] as HTMLElement).focus();
        break;
    }
  };

  const handleYearViewNavigation = (event: React.KeyboardEvent) => {
    const currentFocusedYear = document.activeElement;
    if (!currentFocusedYear || !currentFocusedYear.closest("[data-year]")) {
      // If no year is focused, focus the first available year
      const firstYear = document.querySelector("[data-year]:not([disabled])") as HTMLElement;
      if (firstYear) {
        firstYear.focus();
        return;
      }
      return;
    }

    const yearElements = Array.from(document.querySelectorAll("[data-year]:not([disabled])"));
    const currentIndex = yearElements.indexOf(currentFocusedYear as Element);

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          (yearElements[currentIndex - 1] as HTMLElement).focus();
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < yearElements.length - 1) {
          (yearElements[currentIndex + 1] as HTMLElement).focus();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (currentIndex >= 3) {
          (yearElements[currentIndex - 3] as HTMLElement).focus();
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        if (currentIndex + 3 < yearElements.length) {
          (yearElements[currentIndex + 3] as HTMLElement).focus();
        }
        break;
      case "Home":
        event.preventDefault();
        (yearElements[0] as HTMLElement).focus();
        break;
      case "End":
        event.preventDefault();
        (yearElements[yearElements.length - 1] as HTMLElement).focus();
        break;
    }
  };

  return (
    <div
      className="shadow-xs shadow-sm block rounded-sm border border-slate-300 bg-background p-4 shadow-slate-300"
      aria-label={t("datepicker.calendar")}
      onKeyDown={(e) => handleKeyDown(e, viewMode)}
    >
      <div className="flex flex-row">
        <Section>
          <SectionHeader>
            <div className="flex w-[70%] flex-row items-center justify-between">
              <Button
                className={cn(["w-8", viewMode !== "DAY" ? "disabled cursor-not-allowed text-muted-foreground" : ""])}
                disabled={viewMode !== "DAY"}
                onClick={() => {
                  handleOnOffsetChange({ months: -1 });
                }}
                aria-label={t("datepicker.previousMonth")}
                tabIndex={viewMode === "DAY" ? 0 : -1}
              >
                <IconChevronLeft className="h-3 w-3" aria-hidden="true" />
              </Button>
              <Button
                className="px-3 text-center text-sm"
                onClick={() => {
                  setViewMode("MONTH");
                }}
                aria-label={`${t("datepicker.currentMonth", { month: getLocalizedMonthString(month) })}. ${t("datepicker.clickToSelectMonth")}`}
                aria-haspopup="true"
                aria-expanded={viewMode === "MONTH"}
                tabIndex={0}
              >
                {getLocalizedMonthString(month)}
              </Button>
              <Button
                className={cn(["w-8", viewMode !== "DAY" ? "disabled cursor-not-allowed text-muted-foreground" : ""])}
                disabled={viewMode !== "DAY"}
                onClick={() => {
                  handleOnOffsetChange({ months: 1 });
                }}
                aria-label={t("datepicker.nextMonth")}
                tabIndex={viewMode === "DAY" ? 0 : -1}
              >
                <IconChevronRight className="h-3 w-3" aria-hidden="true" />
              </Button>
            </div>
            <div className="w-1/4">
              <Button
                className="px-3 text-sm"
                onClick={() => {
                  setViewMode("YEAR");
                }}
                aria-label={`${t("datepicker.currentYear", { year: offsetDate.getFullYear() })}. ${t("datepicker.clickToSelectYear")}`}
                aria-haspopup="true"
                aria-expanded={viewMode === "YEAR"}
                tabIndex={0}
              >
                {offsetDate.getFullYear()}
              </Button>
            </div>
          </SectionHeader>

          {viewMode === "DAY" && (
            <>
              <Calendar className="mb-2 h-8 items-center" role="rowgroup" aria-label={t("datepicker.weekDays")}>
                {weekDays.map((d) => (
                  <p key={d} className="text-center text-xs" role="columnheader" aria-label={d}>
                    {d}
                  </p>
                ))}
              </Calendar>
              <Calendar role="grid" aria-label={`${getLocalizedMonthString(month)} ${offsetDate.getFullYear()}`}>
                {days.map((d, index) => {
                  // Use roving tabindex pattern - only the first non-disabled day should be tabbable
                  const isFirstFocusableDay = !d.disabled && days.slice(0, index).every((day) => day.disabled);
                  return (
                    <Button
                      key={d.$date.toString()}
                      className={getDayClassName("w-8 text-xs", d)}
                      onClick={() => {
                        setSelectedDateInternal(d.$date);
                        setOffsetDate(d.$date);
                        setSelectedDate(d.$date);
                      }}
                      data-day={d.$date.toISOString()}
                      aria-label={`${d.day} ${getLocalizedMonthString(month)} ${offsetDate.getFullYear()}`}
                      aria-selected={selectedDateInternal?.toDateString() === d.$date.toDateString()}
                      role="gridcell"
                      tabIndex={d.disabled ? -1 : isFirstFocusableDay ? 0 : -1}
                      disabled={d.disabled}
                    >
                      {d.day}
                    </Button>
                  );
                })}
              </Calendar>
            </>
          )}

          {viewMode === "MONTH" && (
            <div className="grid grid-cols-3 items-center gap-x-2 gap-y-2" role="grid" aria-label={t("datepicker.selectMonth")}>
              {months.map((_m) => {
                // Note: There is a bug where disabled isn't calculated correctly for the month view,
                // making it neccessary to calculate the disabled state manually and overwritting
                // the button props

                const m = wrappedMonth(_m);
                return (
                  <Button
                    key={m.month + year}
                    onClick={() => handleMonthSelected(m.$date.getMonth())}
                    className={getMonthClassName("text-xs", m)}
                    data-month={m.$date.getMonth()}
                    aria-label={`${m.month} ${year}`}
                    aria-disabled={m.disabled}
                    role="gridcell"
                    tabIndex={m.disabled ? -1 : 0}
                    disabled={m.disabled}
                  >
                    {m.month}
                  </Button>
                );
              })}
            </div>
          )}

          {viewMode === "YEAR" && (
            <div
              className="relative mx-2 grid grid-cols-3 items-center gap-x-2 gap-y-2"
              role="grid"
              aria-label={t("datepicker.selectYear")}
            >
              {years.map((y) => (
                <Button
                  key={y.$date.toString()}
                  className={getYearsClassName("text-xs", y)}
                  onClick={() => {
                    handleYearSelected(y.$date.getFullYear());
                  }}
                  data-year={y.$date.getFullYear()}
                  aria-label={t("datepicker.yearLabel", { year: y.year })}
                  aria-disabled={y.disabled}
                  role="gridcell"
                  tabIndex={y.disabled ? -1 : 0}
                  disabled={y.disabled}
                >
                  {y.year}
                </Button>
              ))}
              <div
                className="absolute bottom-0 left-0 top-0 flex flex-col justify-center pl-2 pt-2"
                {...previousYearsButton()}
              >
                <Button className="w-4" {...previousYearsButton()} aria-label={t("datepicker.previousYears")} tabIndex={0}>
                  <IconChevronLeft className="h-3 w-3" aria-hidden="true" />
                </Button>
              </div>
              <div
                className="absolute bottom-0 right-0 top-0 flex flex-col justify-center pr-2 pt-2"
                {...nextYearsButton()}
              >
                <Button className="w-4" {...nextYearsButton()} aria-label={t("datepicker.nextYears")} tabIndex={0}>
                  <IconChevronRight className="h-3 w-3" aria-hidden="true" />
                </Button>
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

export default Datepicker;
